import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { FORM_JOB_TYPES } from "@constants";
import utils from "@utils";
import { ScrollDetect } from "../../../../../components";

import "./ScheduleView.scss";

const AgendaView = ({ jobs, onScrollDetect, hasMore, timezone, user }) => {
	const { lang } = global;
	const [startDates, setStartDates] = useState([]);

	useEffect(() => {
		const newDates = [
			...new Set(jobs.map(({ StartDateTime }) => utils.formatDate(`${StartDateTime}Z`))),
		];
		setStartDates(newDates);
	}, [jobs.length]);

	/**
	 * Determines whether the job starts on the provided date
	 * @param {String} startDate The date on which a job occurs
	 * @param {Object} job The data object representing a job
	 * @returns {Boolean} `true` if job starts on given date, `false` otherwise
	 */
	const doesJobStartOnDate = (startDate, job) => {
		const { StartDateTime } = job;
		const startDateMs = new Date(`${startDate}T00:00:00`).getTime();
		const nextDayMs = startDateMs + 1000 * 60 * 60 * 24;
		const jobStartMs = new Date(`${StartDateTime}Z`).getTime();
		return jobStartMs >= startDateMs && jobStartMs < nextDayMs;
	};

	/**
	 * @param {Object} job The data object representing a job
	 * @returns {Array} An array of all officers assigned to a job (RollKall and external)
	 */
	const allAssignedOfficers = job => {
		const { Jobs: positions } = job;

		const assignedOfficers = positions
			.filter(item => item.AssignedOfficers.length > 0)
			.map(item => item.AssignedOfficers)
			.reduce((acc, item) => [...acc, ...item], [])
			.map(item => ({
				...item.OfficerProfile,
				OfficerName: `${item.OfficerProfile.FirstName} ${item.OfficerProfile.LastName}`,
				OfficerStatus: item.OfficerStatus,
				UserId: item.UserId,
			}));

		const externalOfficers = positions
			.filter(item => item.ExternalOfficers.length > 0)
			.map(item => item.ExternalOfficers)
			.reduce((acc, item) => [...acc, ...item], [])
			.map(item => ({ ...item, isExternal: true }));

		return [...assignedOfficers, ...externalOfficers];
	};

	return (
		<div id="schedule-view">
			<div className="visible-print text-right">
				{utils.formatDate(
					new Date(),
					"en-US",
					undefined,
					user.UserPreferences === "useMilitaryTime" ? "dt-num-mil" : "dt-num",
				)}
			</div>
			<For
				each="date"
				of={startDates}
			>
				<div
					className="new-date"
					key={`jobstart_${date}`}
				>
					<div className="row">
						<div className="col-sm-12">
							<h4>
								{utils.formatDate(
									new Date(`${date}T00:00:00`),
									"en-US",
									undefined,
									"w-long",
								)}
							</h4>
						</div>
					</div>
					<For
						each="job"
						of={jobs}
					>
						<If condition={doesJobStartOnDate(date, job)}>
							<div
								className="row"
								key={`scheduledJob-${job.Id}`}
							>
								<div className="col-sm-2 col-md-2 col-lg-1 job-start text-right hidden-xs">
									<strong>
										{utils.formatTime(
											new Date(`${job.StartDateTime}Z`),
											user.UserPreferences !== "useMilitaryTime",
											timezone,
										)}
									</strong>
									<br />
									{utils.formatTime(
										new Date(`${job.EndDateTime}Z`),
										user.UserPreferences !== "useMilitaryTime",
										timezone,
									)}
								</div>
								<div className="col-sm-7 col-md-7 col-lg-8 col-xs-offset-1 col-sm-offset-0">
									<Link
										to={`/jobs/${job.Id}`}
										key={`job-${job.Id}`}
									>
										<strong>
											{job.Name} - {job.ClientName} (#{job.Id})
										</strong>
									</Link>
									<br className="visible-xs" />
									<Choose>
										<When condition={job.ApplicantsAssigned === 0}>
											<span className="label label-danger">
												{lang.t("Unstaffed")}
											</span>
										</When>
										<When
											condition={
												job.ApplicantsAssigned < job.ApplicantsNeeded
											}
										>
											<span className="label label-warning">
												{lang.t("Understaffed")}
											</span>
										</When>
										<When
											condition={
												job.ApplicantsAssigned === job.ApplicantsNeeded &&
												job.ApplicantsAssigned !== 0
											}
										>
											<span className="label label-success">
												{lang.t("Staffed")}
											</span>
										</When>
									</Choose>
									<br />
									<em>
										{job.LocationAddress}
										{job.LocationAddress && job.LocationCity ? ", " : ""}
										{job.LocationCity}
										{job.LocationCity && job.LocationState ? ", " : ""}
										{job.LocationState}
									</em>
								</div>
								<div className="col-sm-3 job-times col-xs-offset-1 col-sm-offset-0">
									<If
										condition={(job.Jobs || []).some(
											position => position.FlexJob,
										)}
									>
										<span
											className="label label-info"
											title={
												job.JobEventTypeId === FORM_JOB_TYPES.EVENT.value
													? lang.t("This event contains flexible shifts")
													: `${job.Jobs.find(pos => pos.FlexJob).FlexJobMinHours} ${lang.t("Hr Minimum")}`
											}
										>
											{lang.t("FLEX")}
										</span>
										&nbsp;
									</If>
									<strong>{`${job.ApplicantsAssigned}/${job.ApplicantsNeeded} ${lang.t("Staffed")}`}</strong>
									<For
										each="officer"
										of={allAssignedOfficers(job)}
									>
										<div
											className="scheduled-officer"
											key={`assigned-${officer.isExternal ? `e-${officer.Id}` : officer.UserId}`}
										>
											{officer.OfficerName}
										</div>
									</For>
								</div>
							</div>
						</If>
					</For>
				</div>
			</For>
			<If condition={hasMore}>
				<ScrollDetect
					className="text-center"
					onVisible={onScrollDetect}
				>
					<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
				</ScrollDetect>
			</If>
		</div>
	);
};

AgendaView.propTypes = {
	jobs: PropTypes.array,
	onScrollDetect: PropTypes.func,
	hasMore: PropTypes.bool,
	timezone: PropTypes.string,
	user: PropTypes.any.isRequired,
};

AgendaView.defaultProps = {
	jobs: [],
	onScrollDetect: () => {},
	hasMore: true,
	timezone: undefined,
};

export default AgendaView;
